import { useMemo, useCallback, useRef, useState, Fragment } from 'react';
import { DataGrid, TinyButton, DateFilter, DropdownFilter } from '@lendica/components';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuList from '@mui/material/MenuList';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ValueFormatterParams, ICellRendererParams, GridApi } from 'ag-grid-community';
import {
    safeDateFormatter,
    OfferTermsInvitation,
    OfferTermsInvitationRowStatus,
    FilterConfig,
    createDateFilterConfig,
    createDropdownFilterConfig,
    applyFiltersFromUrl,
} from '@lendica/utils';
import { AgGridReact } from 'ag-grid-react';
import { RowAction } from '../hooks/useOfferTermsActions';

const STATUS_FILTER_PARAM = 'status';
const UPDATED_AT_FILTER_PARAM = 'updatedAt';

const STATUS_OPTIONS = [
    OfferTermsInvitationRowStatus.NewDraft,
    OfferTermsInvitationRowStatus.InvitationSent,
    OfferTermsInvitationRowStatus.ApplicationStarted,
    OfferTermsInvitationRowStatus.ApplicationSubmitted,
    OfferTermsInvitationRowStatus.CustomerApproved,
    OfferTermsInvitationRowStatus.CustomerRejected,
];

const FILTER_CONFIGS: FilterConfig[] = [
    { param: STATUS_FILTER_PARAM, column: 'status_display', type: 'dropdown' },
    { param: UPDATED_AT_FILTER_PARAM, column: 'updated_at', type: 'date' },
];

interface InvitedCustomersTableProps {
    rowData: OfferTermsInvitation[];
    gridRef: React.RefObject<AgGridReact>;
    useGridPagination: boolean;
    paginationPageSize: number;
    getActionsForRow: (row: OfferTermsInvitation) => RowAction[];
    onFilterChanged?: () => void;
}

export const InvitedCustomersTable = ({
    rowData,
    gridRef,
    useGridPagination = true,
    paginationPageSize = 20,
    getActionsForRow,
    onFilterChanged,
}: InvitedCustomersTableProps) => {
    const gridApiRef = useRef<GridApi | null>(null);
    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            filter: true,
        };
    }, []);
    const renderUpdatedAtCell = useCallback((params: ICellRendererParams<OfferTermsInvitation>) => {
        const formattedUpdatedAt =
            params.valueFormatted ??
            (params.value ? safeDateFormatter(params.value as string, 'MMM d, yyyy') : '-');

        const expiresAtRaw = (params.data as OfferTermsInvitation & { expires_at?: string | null })
            ?.expires_at;
        const thresholdMs = 72 * 60 * 60 * 1000;
        let showExpiryInfo = false;
        let tooltipTitle = '';

        if (expiresAtRaw) {
            const expiresAt = new Date(expiresAtRaw);
            const diffMs = expiresAt.getTime() - Date.now();

            if (diffMs > 0 && diffMs <= thresholdMs) {
                showExpiryInfo = true;
                tooltipTitle = `Invitation expires ${safeDateFormatter(
                    expiresAtRaw,
                    'MMM d, yyyy h:mm a'
                )}`;
            }
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0.5,
                }}
            >
                <span>{formattedUpdatedAt}</span>
                {showExpiryInfo && (
                    <Tooltip title={tooltipTitle} placement="top">
                        <IconButton size="small" color="warning">
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }, []);

    const colDefs = useMemo(
        () => [
            { field: 'company_name', headerName: 'Company' },
            {
                field: 'email',
                headerName: 'Email',
            },
            {
                field: 'status_display',
                headerName: 'Status',
                sort: 'desc' as const,
                filter: createDropdownFilterConfig(
                    STATUS_FILTER_PARAM,
                    STATUS_OPTIONS,
                    DropdownFilter
                ),
                suppressSpanHeaderHeight: true,
            },
            {
                field: 'updated_at',
                headerName: 'Last updated',
                type: ['date'],
                sort: 'desc' as const,
                cellRenderer: renderUpdatedAtCell,
                filter: createDateFilterConfig(UPDATED_AT_FILTER_PARAM, DateFilter),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: ['actions', 'rightAligned'],
                flex: 1,
                minWidth: 300,
                suppressHeaderFilterButton: true,
                suppressHeaderContextMenu: true,
                suppressHeaderMenuButton: true,
                suppressSpanHeaderHeight: true,
            },
        ],
        [renderUpdatedAtCell]
    );

    const columnTypes = useMemo(() => {
        return {
            date: {
                valueFormatter: (params: ValueFormatterParams) => {
                    return safeDateFormatter(params.value as string, 'MMM d, yyyy');
                },
            },
            actions: {
                cellRenderer: (params: ICellRendererParams<OfferTermsInvitation>) => {
                    const rowActions = getActionsForRow(params.data as OfferTermsInvitation);
                    const inlineActions = rowActions.filter(a => !a.inMenu);
                    const menuActions = rowActions.filter(a => a.inMenu);

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                                width: '100%',
                                height: '100%',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                            }}
                        >
                            {inlineActions.map(action => (
                                <TinyButton
                                    key={action.label}
                                    variant={action.variant || 'outlined'}
                                    color={action.color || 'primary'}
                                    onClick={action.onClick}
                                >
                                    {action.label}
                                </TinyButton>
                            ))}
                            {menuActions.length > 0 && (
                                <ContextMenu
                                    actions={menuActions.map(a => ({
                                        label: a.label,
                                        icon: a.icon,
                                        onClick: a.onClick,
                                        type: a.type,
                                    }))}
                                />
                            )}
                        </Box>
                    );
                },
            },
        };
    }, [getActionsForRow]);

    const autoSizeStrategy = useMemo(() => {
        return {
            type: 'fitGridWidth' as const,
            defaultMinWidth: 200,
            columnLimits: [
                {
                    colId: 'company_name',
                },
                {
                    colId: 'email',
                    minWidth: 300,
                },
                {
                    colId: 'actions',
                    minWidth: 300,
                },
            ],
        };
    }, []);

    const sizeToFit = useCallback(() => {
        if (!gridApiRef.current) return;
        gridApiRef.current.sizeColumnsToFit({
            defaultMinWidth: 200,
            columnLimits: [
                { key: 'company_name' },
                { key: 'email', minWidth: 300 },
                { key: 'actions', minWidth: 300 },
            ],
        });
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        sizeToFit();
        applyFiltersFromUrl(params.api, FILTER_CONFIGS);
    }, [sizeToFit]);

    return (
        <DataGrid
            ref={gridRef}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            rowData={rowData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            onGridSizeChanged={sizeToFit}
            onFilterChanged={onFilterChanged}
            pagination={useGridPagination}
            paginationPageSize={paginationPageSize}
            overlayNoRowsTemplate={`<span>No invited customers found.</span>`}
        />
    );
};

// RowAction type is imported from the hook

const ContextMenu = ({ actions }: { actions: RowAction[] }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    if (actions.length === 0) {
        return null;
    }
    return (
        <Fragment>
            <IconButton
                size="small"
                color="info"
                sx={{
                    borderRadius: '2px',
                }}
                onClick={e => setAnchorEl(e.currentTarget)}
            >
                <MoreVertIcon fontSize="inherit" />
            </IconButton>
            <MenuList dense={true}>
                <Menu
                    variant="menu"
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    anchorEl={anchorEl}
                    sx={{
                        '& .MuiPaper-root': {
                            border: '1px solid #ddd',
                            backgroundColor: 'color-mix(in srgb, #FCFCFD, #202024 2%)',
                            fontSize: '12.5px',
                        },
                    }}
                >
                    {actions.map(action => {
                        return (
                            <div key={action.label}>
                                <MenuItem
                                    onClick={() => {
                                        setAnchorEl(null);
                                        action.onClick();
                                    }}
                                >
                                    {action.icon && (
                                        <ListItemIcon>
                                            <action.icon fontSize="small" />
                                        </ListItemIcon>
                                    )}
                                    <ListItemText>{action.label}</ListItemText>
                                </MenuItem>
                            </div>
                        );
                    })}
                </Menu>
            </MenuList>
        </Fragment>
    );
};
